<script setup lang="ts">
import ExampleBlock from '@/vue/molecules/components/helpers/example-block.vue';
import {useRequiredRule} from '@/ts/composables/pure/use-input-rules';
import type {ComponentTheme} from '@/ts/types/component/layout-component-types';
import {useI18n} from 'vue-i18n';
import {computed} from 'vue';

const {t} = useI18n();

const props = defineProps<{
  name: string;
  exampleHtml?: string | null;
  descriptionHtml?: string | null;
  theme: ComponentTheme;
}>();

const model = defineModel<string>();
const requiredRule = useRequiredRule();

const SIDE_BY_SIDE_MAX_TEXT_LENGTH = 200;
const isSideBySide = computed(() => {
  const textLength = props.exampleHtml?.replace(/<[^>]*>/gu, '').length ?? 0;
  return textLength > 0 && textLength < SIDE_BY_SIDE_MAX_TEXT_LENGTH;
});

</script>
<template>
  <div class="example-single-input" :class="[theme, {sideBySide: isSideBySide}]">
    <div class="left">
      <div class="instructions">
        <h5 class="title">
          {{ name }}
        </h5>
        <div v-if="descriptionHtml" v-safe-html="descriptionHtml" class="description"/>
      </div>
      <example-block v-if="exampleHtml" class="example" :example-html="exampleHtml"/>
    </div>
    <q-input
      v-model="model"
      class="input"
      :rules="[requiredRule]"
      type="textarea"
      borderless
      :placeholder="t('text_input_hint')"
      autogrow
    />
  </div>
</template>
<style scoped lang="scss">
.example-single-input.card {
  @include q-card-white;
  padding: var(--Spacing-spacing-4);

  @include screen-lg {
    padding: var(--Spacing-spacing-7);
  }
}

.example-single-input {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);
}

.left {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-4);

  .sideBySide & {
    @include screen-lg {
      flex-direction: row;
    }
  }
}

.instructions {
  flex: 1;
}

.example {
  .sideBySide & {
    @include screen-lg {
      flex: 1;
    }
  }
}

.title {
  color: var(--Neutral-UI-neutral-ui-100);
  margin-bottom: var(--Spacing-spacing-4);
}

.input {
  @include q-input-textarea-10;
}
</style>
